'use client'
import { HeyaGreeting, type HeyaHeroCopy } from '@/src/components/common/home/common/heyaHero/heyaGreeting'
import FeatureRenderer from '@/src/components/common/featureRenderer'
import { HOME_HERO_GRADIENT_MOBILE } from '@/src/components/common/home/common/homeHeroBackground'
import ExploreMoreHotelsWrapper from '@/src/components/common/home/exploreMoreHotels'
import HomePromoCardsWrapper from '@/src/components/common/home/homePromoCards'
import HotelSearchForm from '@/src/components/common/home/hotelSearch/searchForm'
import HomepageCarousel from '@/src/components/common/home/homepageCarousel'
import HotelShowcaseWrapper from '@/src/components/common/home/hotelShowcase'
import PopularHotelsWrapper from '@/src/components/common/home/popularHotels'
import TrendingHotelsWrapper from '@/src/components/common/home/trendingHotels'
import { useHotelLastSearchInitialValues } from '@/src/hooks/hotels/useHotelLastSearch'
import { Offer } from '@/src/types/offers'
import { Box, Container, Typography } from '@mui/material'

export interface HotelsHomeMobileProps {
    title: string
    subtitle: string
    offers: Offer[]
    locale: string
    /** Present only on the Heya sub-brand, which replaces the plain heading with a photo hero. */
    heyaCopy?: HeyaHeroCopy
}

// Mobile hotels landing — 1:1 with the RN hotels screen: hero (title + subtitle) and the
// search form card, followed by the offers carousel, featured hotels and trending
// destinations. The form owns its state and navigates to /hotels/results.
const HotelsHomeMobile: React.FC<HotelsHomeMobileProps> = ({ title, subtitle, offers, locale, heyaCopy }) => {
    // localStorage-backed prefill; `ready` remounts the form after mount so SSR stays empty.
    const { initialValues, ready } = useHotelLastSearchInitialValues()

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'neutral.100' }}>
        {heyaCopy ? (
            // HeyaGreeting carries its own mobile backdrop and gutters.
            <HeyaGreeting
                variant="hotels"
                badge={heyaCopy.badge}
                headingLead={heyaCopy.headingLead}
                headingAccent={heyaCopy.headingAccent}
                intro={heyaCopy.intro}
            />
        ) : (
            <Box
                sx={{
                    px: 2,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    background: HOME_HERO_GRADIENT_MOBILE,
                    backgroundSize: '100% 175%'
                }}
            >
                <Typography sx={{ fontSize: 24, fontWeight: 700, color: 'textColors.primary' }}>{title}</Typography>
                <Typography sx={{ fontSize: 15, color: 'textColors.secondary', mt: 0.5 }}>{subtitle}</Typography>
            </Box>
        )}
        <Box sx={{ mx: 2 }}>
            <Box
                sx={{
                    bgcolor: 'neutral.white',
                    border: 1,
                    borderColor: 'borders.default',
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                <HotelSearchForm key={ready ? 'seeded' : 'empty'} isMobile initialValues={initialValues} />
            </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
            <HomepageCarousel offers={offers} locale={locale} isMobile />
            <FeatureRenderer type="HOTEL_HOME_SHOWCASE">
                <HotelShowcaseWrapper />
            </FeatureRenderer>
            <FeatureRenderer type="TRENDING_HOTELS">
                {/* Inset the carousel to the page rail so it starts like the popular-hotels row. */}
                <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                    <TrendingHotelsWrapper />
                </Container>
            </FeatureRenderer>
            <FeatureRenderer type="HOTEL_HOME_SHOWCASE">
                <PopularHotelsWrapper />
                <HomePromoCardsWrapper />
                <ExploreMoreHotelsWrapper />
            </FeatureRenderer>
        </Box>
        </Box>
    )
}

export { HotelsHomeMobile }
export default HotelsHomeMobile
